import React, { useEffect, useState, Fragment, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllAppointmentsByDateRange,
  updateAppointmentStatus,
  updateAppointmentHistory,
  fetchClinicianAppointmentsByDateRange,
  fetchAppointmentById,
} from "../../store/thunks/appointmentsThunk";
import {
  fetchAllPayrollsByStatusAndPayPeriod,
  updatePayroll,
} from "../../store/thunks/payrollThunk";
import PayrollTable from "../../applicationUi/components/PayrollTable";
import CompletedPayrollTable from "../../applicationUi/components/CompletedPayrollTable";
import PayrollScheduleTable from "./PayrollSchedule";
import PayrollSlideOver from "./PayrollSlideOver";
import payrollDates from "./payrollDates";
import PayrollStats from "./PayrollStats";
import CustomPayrollModal from "./CustomPayrollModal";
import StatusLights from "../../applicationUi/components/StatusLights";
import * as XLSX from "xlsx";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { weekdays } from "moment/moment";

export default function HRIndividualPayrollPage({
  clinician,
  selectedPeriod: initialSelectedPeriod,
  isOpen,
  onClose,
}) {
  console.log("received clincian", clinician);
  console.log("IN HR INDIVIDUAL PAYROLL PAGE");

  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const clinicianPayrollAppointments = useSelector(
    (state) => state.appointments.clinicianPayrollAppointments
  );
  const loading = useSelector((state) => state.appointments.loading);

  // State management
  const [uiState, setUiState] = useState({
    isCustomModalOpen: false,
    isPayrollSlideOver: false,
    showPayrollSchedule: false,
    isLoading: false,
  });

  const [payrollState, setPayrollState] = useState({
    regularPayrolls: [],
    customPayrolls: [],
    carryoverAppointments: [],
    completedAppointments: [],
    localAppointments: [],
    pendingAppointments: [],
    editingPayroll: null,
    selectedAppointment: null,
    soapNotesComplete: false,
    invoicePaid: false,
    eligibleForPayroll: false,
  });

  const [selectedPeriodDates, setSelectedPeriodDates] = useState(null);

  // Helper function for class names
  const classNames = (...classes) => {
    return classes.filter(Boolean).join(" ");
  };

  // Initialize selectedPeriodDates when initialSelectedPeriod changes
  useEffect(() => {
    if (initialSelectedPeriod?.dates) {
      const [start, end] = initialSelectedPeriod.dates
        .split(" - ")
        .map((date) => new Date(`${date.trim()}`));
      setSelectedPeriodDates({ start, end });
    }
  }, [initialSelectedPeriod]);

  // Fetch appointments when period changes
  useEffect(() => {
    if (clinician?._id && initialSelectedPeriod?.endDate) {
      dispatch(
        fetchClinicianAppointmentsByDateRange({
          clinicianId: clinician._id,
          endDate: initialSelectedPeriod.endDate,
        })
      );
    }
  }, [dispatch, clinician, initialSelectedPeriod]);

  // Process appointments data when it's available
  const appointments = useMemo(() => {
    if (!clinicianPayrollAppointments || !clinician?._id) return [];
    const clinicianData = clinicianPayrollAppointments[clinician._id];
    return clinicianData?.appointments || [];
  }, [clinicianPayrollAppointments, clinician]);

  // Update payroll categorization when completedAppointments changes
  useEffect(() => {
    const { completedAppointments } = payrollState;

    const filteredRegular = completedAppointments
      .flatMap((item) =>
        item.payrolls
          .filter((payroll) => !payroll.isCustom)
          .map((payroll) => ({
            ...payroll,
            clinician: item.clinician,
          }))
      )
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    const filteredCustom = completedAppointments
      .flatMap((item) =>
        item.payrolls
          .filter((payroll) => payroll.isCustom)
          .map((payroll) => ({
            ...payroll,
            clinician: item.clinician,
          }))
      )
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    setPayrollState((prev) => ({
      ...prev,
      regularPayrolls: filteredRegular,
      customPayrolls: filteredCustom,
    }));
  }, [payrollState.completedAppointments]);

  // Destructure state for easier access in JSX
  const {
    isCustomModalOpen,
    isPayrollSlideOver,
    showPayrollSchedule,
    isLoading,
  } = uiState;

  const {
    regularPayrolls,
    customPayrolls,
    carryoverAppointments,
    completedAppointments,
    localAppointments,
    pendingAppointments,
    editingPayroll,
    selectedAppointment,
    soapNotesComplete,
    invoicePaid,
    eligibleForPayroll,
  } = payrollState;

  // Updated fetchData function
  const fetchData = async () => {
    console.log("fetch data called");
    if (initialSelectedPeriod && user) {
      setUiState((prev) => ({ ...prev, isLoading: true }));

      const [start, end] = initialSelectedPeriod.includeDates
        .split("-")
        .map((date) => new Date(`${date.trim()}`));

      setSelectedPeriodDates({ start, end });

      const currentDate = new Date();
      const submissionDate = new Date(
        `${initialSelectedPeriod.submissionDate}`
      );

      try {
        // Fetch appointments
        end.setHours(23, 59, 59, 999);

        const appointmentsResult = await dispatch(
          // fetchAllAppointmentsByDateRange({ endDate: end })
          fetchClinicianAppointmentsByDateRange({
            clinicianId: clinician._id,
            endDate: end,
          })
        ).unwrap();

        console.log("end date", end);

        // Extract appointments for the current clinician
        const clinicianAppointments =
          appointmentsResult[clinician._id]?.appointments || [];

        // Fetch payrolls
        const payrollsResult = await dispatch(
          fetchAllPayrollsByStatusAndPayPeriod({
            statuses: [
              "Pending",
              "Ready",
              "Approved",
              "Paid to Clinician",
              "Carryover",
            ],
            payPeriod: initialSelectedPeriod.payPeriodNumber,
          })
        ).unwrap();

        // Filter payrolls to only include those for the current clinician
        const clinicianPayrolls = payrollsResult.filter(
          (payroll) => payroll.clinician?._id === clinician._id
        );

        // Categorize appointments
        const carryoverResult =
          currentDate > submissionDate ? clinicianAppointments : [];

        // Update all state at once
        setPayrollState((prev) => ({
          ...prev,
          localAppointments: clinicianAppointments,
          carryoverAppointments: carryoverResult,
          completedAppointments: clinicianPayrolls,
          selectedAppointment: selectedAppointment
            ? clinicianAppointments.find(
                (appointment) => appointment._id === selectedAppointment._id
              )
            : null,
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setUiState((prev) => ({ ...prev, isLoading: false }));
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [initialSelectedPeriod, clinician._id]);

  // Export payroll data to spreadsheet
  const exportToSpreadsheet = () => {
    const payrollData = [...regularPayrolls, ...customPayrolls].map(
      (payroll) => ({
        "Clinician Name": `${payroll.clinician.firstName} ${payroll.clinician.lastName}`,
        "Pay Period": payroll.payPeriod,
        Amount: payroll.payrollAmount,
        Status: payroll.status,
        "Created At": new Date(payroll.createdAt).toLocaleDateString(),
        "Updated At": new Date(payroll.updatedAt).toLocaleDateString(),
      })
    );

    const ws = XLSX.utils.json_to_sheet(payrollData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payroll Data");

    const fileName = `PayPeriod_${initialSelectedPeriod.payPeriodNumber}_PayrollData.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (!clinician || !clinician._id) {
    return (
      <div className="p-4">
        <p className="text-red-500">Error: Clinician data is unavailable.</p>
      </div>
    );
  }

  const normalizeAppointmentData = (data) => {
    let appointment = data.appointmentId ? data.appointmentId : data;
    let invoice = data.invoiceId ? data.invoiceId : data.invoice;
    let payrollId = data._id;
    return { appointment, invoice, payrollId };
  };

  const handleAppointmentClick = async (data) => {
    try {
      // First set loading state
      setUiState((prev) => ({
        ...prev,
        isLoading: true,
      }));

      // Determine the correct appointment ID
      const appointmentId = data.appointment?._id || data._id;

      if (!appointmentId) {
        console.error("No valid appointment ID found in data:", data);
        throw new Error("No valid appointment ID found");
      }

      // Fetch the full appointment details
      const fullAppointment = await dispatch(
        fetchAppointmentById(appointmentId)
      ).unwrap();

      // Set the states with the fetched data
      setPayrollState((prev) => ({
        ...prev,
        selectedAppointment: {
          appointment: fullAppointment,
          invoice: data.invoice, // Keep any existing invoice data
          payrollId: data._id,
        },
      }));

      setUiState((prev) => ({
        ...prev,
        isPayrollSlideOver: true,
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setUiState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleCloseScheduleModal = () => {
    setUiState((prev) => ({
      ...prev,
      showPayrollSchedule: false,
    }));
  };

  const filterAppointments = () => {
    return pendingAppointments
      .filter((appointment) => {
        const hasSoapNotesComplete = appointment.history.some((historyItem) =>
          historyItem.status.includes("Soap Notes Complete")
        );
        const hasInvoicePaid =
          appointment.invoice && appointment.invoice.status.includes("paid");
        const isCancelledNoFee =
          appointment.status.includes("Cancelled - No Fee");
        const isNoShowNoFee = appointment.status.includes("No Show - No Fee");
        const isCancelled = appointment.status.includes("Cancelled");
        const isNoShow = appointment.status.includes("No Show");

        // Exclude cancelled and no show appointments with no fee
        if (isCancelledNoFee || isNoShowNoFee || isCancelled || isNoShow) {
          return false;
        }

        if (eligibleForPayroll) {
          return hasSoapNotesComplete && hasInvoicePaid;
        }
        if (soapNotesComplete && !hasSoapNotesComplete) {
          return false;
        }
        if (invoicePaid && !hasInvoicePaid) {
          return false;
        }
        return true;
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  };

  const getSubmissionDate = () => {
    const currentPeriod = payrollDates.find(
      (period) =>
        period.payPeriodNumber === initialSelectedPeriod.payPeriodNumber
    );

    if (currentPeriod) {
      const submissionDate = new Date(currentPeriod.submissionDate);
      return submissionDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    } else {
      return "N/A";
    }
  };

  const getScheduledPayDay = () => {
    const currentPeriod = payrollDates.find(
      (period) =>
        period.payPeriodNumber === initialSelectedPeriod.payPeriodNumber
    );

    if (currentPeriod) {
      const paymentDate = new Date(currentPeriod.paymentDate);
      return paymentDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    } else {
      return "N/A";
    }
  };

  console.log("customPayrolls", customPayrolls);
  console.log("regularPayrolls", regularPayrolls);

  const calculateIncompleteAppointments = () => {
    return pendingAppointments.filter((appointment) => {
      const hasSoapNotesComplete = appointment.history.some(
        (historyItem) => historyItem.status === "Soap Notes Complete"
      );
      const hasInvoicePaid =
        appointment.invoice && appointment.invoice.status === "paid";

      return !(hasSoapNotesComplete && hasInvoicePaid);
    }).length;
  };

  const calculatePendingAppointments = () => {
    return payrollState.pendingAppointments.length;
  };

  const calculateCompletedAppointments = () => {
    return payrollState.completedAppointments
      .flatMap((item) => item.payrolls)
      .filter((payroll) => !payroll.isCustom).length;
  };

  const calculateCarryoverAppointments = () => {
    console.log("carryoverAppointments", payrollState.carryoverAppointments);
    return payrollState.carryoverAppointments.length;
  };

  const calculatePendingPay = () => {
    const totalServiceCost = pendingAppointments.reduce((sum, appointment) => {
      const servicePrice = appointment.service?.price || 0;
      return sum + servicePrice;
    }, 0);
    const payPercentage = clinician?.defaultPayPercentage || 45;
    return (totalServiceCost * (payPercentage / 100)).toFixed(2);
  };

  const calculateCompletedPay = () => {
    // Sum up payroll amounts for regular payrolls
    const regularPayrollsTotal = regularPayrolls.reduce(
      (sum, payroll) => sum + (payroll.payrollAmount || 0),
      0
    );

    // Sum up payroll amounts for custom payrolls
    const customPayrollsTotal = customPayrolls.reduce(
      (sum, customPayroll) => sum + (customPayroll.payrollAmount || 0),
      0
    );

    // Return the total payroll amount
    return (regularPayrollsTotal + customPayrollsTotal).toFixed(2);
  };

  // Updated calculateCarryoverPay function
  const calculateCarryoverPay = () => {
    if (!Array.isArray(carryoverAppointments)) {
      console.warn(
        "carryoverAppointments is not an array:",
        carryoverAppointments
      );
      return "0.00";
    }

    const totalServiceCost = carryoverAppointments.reduce(
      (sum, appointment) => {
        const serviceCost = appointment.service?.cost || 0;
        return sum + serviceCost;
      },
      0
    );

    const payPercentage = clinician?.defaultPayPercentage || 45;
    return (totalServiceCost * (payPercentage / 100)).toFixed(2);
  };

  const toggleCustomPayrollModal = () => {
    setUiState((prev) => ({
      ...prev,
      isCustomModalOpen: !prev.isCustomModalOpen,
    }));

    if (isCustomModalOpen) {
      setPayrollState((prev) => ({
        ...prev,
        editingPayroll: null,
      }));
    }
  };

  const markAsReady = async () => {
    const confirmAction = window.confirm(
      `Are you sure you want to mark ${regularPayrolls.length} regular payrolls as 'Ready'?`
    );
    if (!confirmAction) {
      return;
    }

    try {
      // Loop through regularPayrolls and update each one
      const updatePromises = regularPayrolls.map(async (payroll) => {
        if (payroll && payroll._id) {
          await dispatch(
            updatePayroll({
              id: payroll._id,
              updateData: { status: "Ready" },
              updatedBy: user._id,
            })
          ).unwrap();
        } else {
          console.warn("Invalid payroll object:", payroll);
        }
      });

      await Promise.all(updatePromises);

      fetchData();

      alert("All regular payrolls have been marked as 'Ready' successfully!");
    } catch (error) {
      console.error("Error updating regular payrolls or appointments:", error);
      alert(
        "An error occurred while updating regular payrolls. Please try again."
      );
      fetchData();
    }
  };

  const markRegularAsPaid = async () => {
    const confirmAction = window.confirm(
      `Are you sure you want to mark ${regularPayrolls.length} regular payrolls as 'Paid to Clinician'?`
    );
    if (!confirmAction) {
      return;
    }

    try {
      // Loop through regularPayrolls and update each one
      const updatePromises = regularPayrolls.map(async (payroll) => {
        if (payroll && payroll._id) {
          // Update payroll status
          await dispatch(
            updatePayroll({
              id: payroll._id,
              updateData: { status: "Paid to Clinician" },
              updatedBy: user._id,
            })
          ).unwrap();

          // Check if payroll has an associated appointmentId
          if (payroll.appointmentId) {
            await dispatch(
              // updateAppointmentHistory({
              //   id: payroll.appointmentId._id,
              //   historyItem: {
              //     status: "Paid to Clinician",
              //     timestamp: new Date().toISOString(),
              //     comments: "Appointment marked as Paid to Clinician",
              //     user: user._id,
              //   },
              // }),
              updateAppointmentStatus({
                appointmentId: payroll.appointmentId._id,
                status: "Paid to Clinician",
                comments: "Status changed to Paid to Clinician",
                userId: user._id,
              })
            ).unwrap();
          } else {
            console.warn(
              `Payroll with ID ${payroll._id} does not have an associated appointmentId. Skipping appointment update.`
            );
          }
        } else {
          console.warn("Invalid payroll object:", payroll);
        }
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      fetchData();

      alert(
        "All regular payrolls have been marked as 'Paid to Clinician' successfully!"
      );
    } catch (error) {
      console.error("Error updating regular payrolls or appointments:", error);
      alert(
        "An error occurred while updating regular payrolls. Please try again."
      );
      fetchData();
    }
  };

  const markCustomAsPaid = async () => {
    const confirmAction = window.confirm(
      `Are you sure you want to mark ${customPayrolls.length} custom payrolls as 'Paid to Clinician'?`
    );
    if (!confirmAction) {
      return;
    }

    try {
      // Loop through customPayrolls and update each one
      const updatePromises = customPayrolls.map(async (payroll) => {
        if (payroll && payroll._id) {
          await dispatch(
            updatePayroll({
              id: payroll._id,
              updateData: { status: "Paid to Clinician" },
              updatedBy: user._id,
            })
          ).unwrap();
        } else {
          console.warn("Invalid payroll object:", payroll);
        }
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      fetchData();

      alert(
        "All custom payrolls have been marked as 'Paid to Clinician' successfully!"
      );
    } catch (error) {
      console.error("Error updating custom payrolls:", error);
      alert(
        "An error occurred while updating custom payrolls. Please try again."
      );
    }
  };

  const stats = [
    {
      name: `Pay Period ${initialSelectedPeriod.payPeriodNumber}`,
      value: `${initialSelectedPeriod.includeDates}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Submit Appointments By End Of",
      value: `${getSubmissionDate()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Pay Day",
      value: `${getScheduledPayDay()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Pending Appointments",
      value: `${calculatePendingAppointments()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Completed Appointments",
      value: `${calculateCompletedAppointments()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Carryover Appointments",
      value: `${calculateCarryoverAppointments()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Total Pending Pay",
      value: `$${calculatePendingPay()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Total Completed Pay",
      value: `$${calculateCompletedPay()}`,
      change: "",
      changeType: "neutral",
    },
    {
      name: "Total Carryover Pay",
      value: `$${calculateCarryoverPay()}`,
      change: "",
      changeType: "neutral",
    },
  ];

  const getStatus = (item) => {
    if (!Array.isArray(item.history)) {
      console.warn("Invalid or missing history for item:", item);
      return {
        statusTags: <div className="text-red-500">No history available</div>,
        isReady: false,
      };
    }

    const canceledOrNoShowStatuses = [
      "Cancelled",
      "Cancelled - No Fee",
      "Cancelled - Charge Fee",
      "No Show",
      "No Show - No Fee",
      "No Show - Charge Fee",
    ];

    // Check if the appointment has any canceled or no-show status
    const canceledOrNoShowHistory = item.history.filter((historyItem) =>
      canceledOrNoShowStatuses.includes(historyItem.status)
    );

    const hasSoapNotesComplete = item.history.some(
      (historyItem) => historyItem.status === "Soap Notes Completed"
    );

    const hasSoapNotesSaved = item.history.some(
      (historyItem) => historyItem.status === "Soap Notes Saved"
    );

    const invoiceStatus = item.invoice ? item.invoice.status : null;

    const statuses = [];
    const statusClasses = {
      "SOAP Notes: Incomplete": "text-red-700 bg-red-50 ring-red-600/20",
      "Soap Notes: Saved": "text-orange-700 bg-orange-50 ring-orange-600/20",
      "Soap Notes Completed": "text-green-700 bg-green-50 ring-green-600/20",
      "Invoice: created": "text-orange-700 bg-orange-50 ring-orange-600/20",
      "Invoice: sent": "text-blue-700 bg-blue-50 ring-blue-600/20",
      "Invoice: paid": "text-green-700 bg-green-50 ring-green-600/20",
      "Invoice: void": "text-red-700 bg-red-50 ring-red-600/20",
      "Invoice: No Invoice": "text-red-700 bg-red-50 ring-red-600/20",
      Ready: "text-green-700 bg-green-50 ring-green-600/20",
      Default: "text-red-700 bg-red-50 ring-red-600/20",
    };

    if (canceledOrNoShowHistory.length > 0) {
      // Add all canceled or no-show statuses
      statuses.push(...canceledOrNoShowHistory.map((item) => item.status));

      // If it's a canceled or no-show appointment, skip SOAP Notes requirements
      if (
        ["Cancelled - Charge Fee", "No Show - Charge Fee"].some((status) =>
          canceledOrNoShowHistory.some(
            (historyItem) => historyItem.status === status
          )
        )
      ) {
        if (!invoiceStatus) statuses.push("Invoice: No Invoice");
      }
    } else {
      // If not canceled or no-show, check SOAP Notes and Invoice
      if (hasSoapNotesComplete) {
        statuses.push("Soap Notes Completed"); // Prioritize "Soap Notes Completed"
      } else if (hasSoapNotesSaved) {
        statuses.push("Soap Notes: Saved"); // Add tag for Soap Notes Saved
      } else {
        statuses.push("SOAP Notes: Incomplete"); // Add orange tag for incomplete
      }

      if (!item.invoice) statuses.push("Invoice: No Invoice");
      else if (invoiceStatus === "created")
        statuses.push("Invoice: created"); // Orange tag for Created
      else if (invoiceStatus === "sent")
        statuses.push("Invoice: sent"); // Blue tag for Sent
      else if (invoiceStatus === "paid")
        statuses.push("Invoice: paid"); // Green tag for Paid
      else if (invoiceStatus === "void") statuses.push("Invoice: void"); // Red tag for Void

      if (hasSoapNotesComplete && invoiceStatus === "paid")
        statuses.push("Ready");
    }

    const statusTags = (
      <div className="flex flex-col items-center space-y-1">
        {statuses.map((status, index) => (
          <div
            key={index}
            className={classNames(
              statusClasses[status] || statusClasses["Default"],
              "rounded-sm px-2 py-1 text-xs font-medium ring-1 ring-inset"
            )}>
            {status}
          </div>
        ))}
      </div>
    );

    return {
      statusTags,
      isReady: hasSoapNotesComplete && invoiceStatus === "paid",
    };
  };

  const getTimeUntilPayday = () => {
    const currentPeriod = payrollDates.find(
      (period) =>
        period.payPeriodNumber === initialSelectedPeriod.payPeriodNumber
    );

    if (currentPeriod) {
      const paymentDate = new Date(currentPeriod.paymentDate);
      const now = new Date();
      const timeDifference = paymentDate - now;

      if (timeDifference > 0) {
        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDifference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((timeDifference / 1000 / 60) % 60);
        return { days, hours, minutes, isPaydayReached: false };
      } else {
        return { days: 0, hours: 0, minutes: 0, isPaydayReached: true };
      }
    } else {
      return { days: 0, hours: 0, minutes: 0, isPaydayReached: false };
    }
  };

  const { days, hours, minutes, isPaydayReached } = getTimeUntilPayday();

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
          </div>
        )}

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full">
                <Dialog.Panel className="pointer-events-auto w-screen max-w-8xl pl-10">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Header section - More compact */}
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <Dialog.Title className="text-lg font-medium text-gray-900">
                        {clinician.firstName} {clinician.lastName}
                      </Dialog.Title>

                      <div className="flex gap-2 flex-wrap items-center">
                        <button
                          type="button"
                          onClick={exportToSpreadsheet}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50">
                          <svg
                            className="h-4 w-4 mr-1.5 text-gray-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                            />
                          </svg>
                          Export
                        </button>
                        <button
                          type="button"
                          onClick={toggleCustomPayrollModal}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded shadow-sm hover:bg-indigo-500">
                          Add Custom Payroll
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setUiState((prev) => ({
                              ...prev,
                              showPayrollSchedule: true,
                            }))
                          }
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50">
                          View Schedule
                        </button>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-500"
                          onClick={onClose}>
                          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {/* Main content area - Streamlined */}
                    <div className="flex-1 px-4 pb-10">
                      <div className="py-4">
                        <PayrollStats stats={stats} />
                      </div>

                      {/* Tables section - More compact */}
                      <div className="space-y-4">
                        {/* Regular Payrolls */}
                        <div className="border border-gray-200 rounded shadow-sm">
                          <CompletedPayrollTable
                            columns={[
                              {
                                header: "Payroll Status",
                                accessor: (item) => `${item.status}`,
                              },
                              {
                                header: "Appt Status",
                                accessor: (item) =>
                                  item.appointment ? (
                                    <StatusLights
                                      appointment={item.appointment}
                                    />
                                  ) : (
                                    "N/A"
                                  ),
                              },
                              {
                                header: "Client",
                                accessor: (item) =>
                                  item.appointment && item.appointment.client
                                    ? `${item.appointment.client.firstName} ${item.appointment.client.lastName}`
                                    : "N/A",
                              },
                              {
                                header: "Appt Service",
                                accessor: (item) =>
                                  item.appointment && item.appointment.service
                                    ? item.appointment.service.description
                                    : "N/A",
                              },
                              {
                                header: "Service Date",
                                accessor: (item) => {
                                  const rawDate = item.appointment?.start;
                                  if (rawDate) {
                                    const date = new Date(rawDate);
                                    const optionsDate = {
                                      year: "numeric",
                                      month: "short",
                                      weekday: "short",
                                      day: "numeric",
                                    };
                                    const optionsTime = {
                                      hour: "numeric",
                                      minute: "numeric",
                                      hour12: true,
                                    };
                                    return `${date.toLocaleDateString(
                                      "en-US",
                                      optionsDate
                                    )}, ${date.toLocaleTimeString(
                                      "en-US",
                                      optionsTime
                                    )}`;
                                  }
                                  return "N/A";
                                },
                                sortValue: (item) => {
                                  const rawDate = item.appointment?.start;
                                  return rawDate
                                    ? new Date(rawDate).getTime()
                                    : 0;
                                },
                              },
                              {
                                header: "Invoice",
                                accessor: (item) =>
                                  item.invoice?.invoiceId || "N/A",
                              },
                              {
                                header: "Invoice Service",
                                accessor: (item) =>
                                  item.invoice?.services
                                    ? item.invoice.services
                                        .map((service) => service.description)
                                        .join(", ")
                                    : "N/A",
                              },
                              {
                                header: "Billed Amt",
                                accessor: (item) =>
                                  item.originalAmount !== undefined
                                    ? `$${item.originalAmount.toFixed(2)}`
                                    : "N/A",
                              },
                              {
                                header: "% Received",
                                accessor: (item) =>
                                  item.percentReceived !== undefined
                                    ? `${item.percentReceived}%`
                                    : "N/A",
                              },
                              {
                                header: "Payroll Amt",
                                accessor: (item) =>
                                  item.payrollAmount !== undefined
                                    ? `$${item.payrollAmount.toFixed(2)}`
                                    : "N/A",
                              },
                            ]}
                            data={regularPayrolls}
                            onRowClick={handleAppointmentClick}
                            clinicianId={null}
                            title={`Regular Payrolls (${regularPayrolls.length})`}
                            description="Regular completed appointments for this pay period"
                            actions={[
                              {
                                label: `Mark ${regularPayrolls.length} as Ready`,
                                onClick: markAsReady,
                                className: "px-3 py-1.5 text-sm",
                              },
                              {
                                label: `Mark ${regularPayrolls.length} as Paid`,
                                onClick: isPaydayReached
                                  ? markRegularAsPaid
                                  : null,
                                disabled: !isPaydayReached,
                                className: "px-3 py-1.5 text-sm",
                              },
                            ]}
                            countdown={
                              !isPaydayReached
                                ? `Time until payday: ${days}d ${hours}h ${minutes}m`
                                : null
                            }
                          />
                        </div>

                        {/* Custom Payrolls */}
                        <div className="border border-gray-200 rounded shadow-sm">
                          <CompletedPayrollTable
                            columns={[
                              {
                                header: "Payroll Status",
                                accessor: (item) => `${item.status}`,
                              },
                              {
                                header: "Description",
                                accessor: (item) =>
                                  item.description ? item.description : "N/A",
                              },
                              {
                                header: "Payroll Amount",
                                accessor: (item) =>
                                  item.payrollAmount !== undefined
                                    ? `$${item.payrollAmount.toFixed(2)}`
                                    : "N/A",
                              },
                            ]}
                            data={customPayrolls}
                            onRowClick={handleAppointmentClick}
                            actions={[
                              {
                                label: `Mark ${customPayrolls.length} as Paid`,
                                onClick: isPaydayReached
                                  ? markCustomAsPaid
                                  : null,
                                disabled: !isPaydayReached,
                                className: "px-3 py-1.5 text-sm",
                              },
                            ]}
                            clinicianId={null}
                            title={`Custom Payrolls (${customPayrolls.length})`}
                            description="Custom payroll items for this period"
                          />
                        </div>

                        {/* Pending Appointments */}
                        {filterAppointments().length > 0 && (
                          <div className="border border-gray-200 rounded shadow-sm">
                            <PayrollTable
                              columns={[
                                {
                                  header: "Appt Status",
                                  accessor: (item) =>
                                    getStatus(item).statusTags,
                                },
                                {
                                  header: "Client",
                                  accessor: (item) =>
                                    item.client
                                      ? `${item.client.firstName} ${item.client.lastName}`
                                      : "N/A",
                                },
                                {
                                  header: "Service",
                                  accessor: (item) =>
                                    item.service
                                      ? item.service.description
                                      : "N/A",
                                },
                                {
                                  header: "Service Date",
                                  accessor: (item) => {
                                    if (item.start) {
                                      const date = new Date(item.start);
                                      const optionsDate = {
                                        year: "numeric",
                                        month: "short",
                                        weekday: "short",
                                        day: "numeric",
                                      };
                                      const optionsTime = {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                      };
                                      return `${date.toLocaleDateString(
                                        "en-US",
                                        optionsDate
                                      )}, ${date.toLocaleTimeString(
                                        "en-US",
                                        optionsTime
                                      )}`;
                                    }
                                    return "N/A";
                                  },
                                  sortBy: (item) => {
                                    if (item.start) {
                                      const date = new Date(item.start);
                                      return isNaN(date) ? null : date; // Return null if date is invalid
                                    }
                                    return null;
                                  },
                                },
                                {
                                  header: "Invoice ID",
                                  accessor: (item) =>
                                    item.invoice
                                      ? item.invoice.invoiceId
                                      : "No Invoice",
                                },
                                {
                                  header: "Invoice Amount",
                                  accessor: (item) =>
                                    item.invoice
                                      ? `$${item.invoice.amount.toFixed(2)}`
                                      : "No Invoice",
                                },
                                {
                                  header: "Invoice Status",
                                  accessor: (item) =>
                                    item.invoice
                                      ? item.invoice.status
                                      : "No Invoice",
                                },
                                {
                                  header: "Invoice Created",
                                  accessor: (item) =>
                                    item.invoice
                                      ? new Date(
                                          item.invoice.createdAt
                                        ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "short",
                                          weekday: "short",
                                          day: "numeric",
                                        })
                                      : "No Invoice",
                                },
                              ]}
                              data={filterAppointments()}
                              onRowClick={handleAppointmentClick}
                              actions={[]}
                              title={`Pending (${calculateIncompleteAppointments()})`}
                              description="Incomplete appointments for this period"
                            />
                          </div>
                        )}

                        {/* Carryover Appointments */}
                        {carryoverAppointments.length > 0 && (
                          <div className="border border-gray-200 rounded shadow-sm">
                            <PayrollTable
                              columns={[
                                {
                                  header: "Appt Status",
                                  accessor: (item) =>
                                    getStatus(item).statusTags,
                                },
                                // {
                                //   header: "Appointment History",
                                //   accessor: (item) =>
                                //     item.history && item.history.length > 0
                                //       ? item.history
                                //           .map((entry) => entry.status)
                                //           .join(", ") // Joins all the statuses into a single string
                                //       : "No History",
                                // },
                                {
                                  header: "Client",
                                  accessor: (item) =>
                                    item.client
                                      ? `${item.client.firstName} ${item.client.lastName}`
                                      : "N/A",
                                },
                                {
                                  header: "Service",
                                  accessor: "service.description",
                                },
                                {
                                  header: "Service Date",
                                  accessor: (item) => {
                                    if (item.start) {
                                      const date = new Date(item.start);
                                      const optionsDate = {
                                        year: "numeric",
                                        month: "short",
                                        weekday: "short",
                                        day: "numeric",
                                      };
                                      const optionsTime = {
                                        hour: "numeric",
                                        minute: "numeric",
                                        hour12: true,
                                      };
                                      return `${date.toLocaleDateString(
                                        "en-US",
                                        optionsDate
                                      )}, ${date.toLocaleTimeString(
                                        "en-US",
                                        optionsTime
                                      )}`;
                                    } else {
                                      return "N/A";
                                    }
                                  },
                                },
                                {
                                  header: "Invoice ID",
                                  accessor: (item) =>
                                    item.invoice
                                      ? item.invoice.invoiceId
                                      : "No Invoice",
                                },
                                {
                                  header: "Invoice Amount",
                                  accessor: (item) =>
                                    item.invoice
                                      ? `$${item.invoice.amount.toFixed(2)}`
                                      : "No Invoice",
                                },
                                {
                                  header: "Invoice Status",
                                  accessor: (item) =>
                                    item.invoice
                                      ? item.invoice.status
                                      : "No Invoice",
                                },
                                {
                                  header: "Invoice Created",
                                  accessor: (item) =>
                                    item.invoice
                                      ? new Date(
                                          item.invoice.createdAt
                                        ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "short",
                                          weekday: "short",
                                          day: "numeric",
                                        })
                                      : "No Invoice",
                                },
                              ]}
                              data={carryoverAppointments}
                              onRowClick={handleAppointmentClick}
                              actions={[]}
                              title={`Carryover (${calculateCarryoverAppointments()})`}
                              description="Appointments carried over from previous periods"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>

        {/* Modals - Simplified styling */}
        {showPayrollSchedule && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded w-full max-w-4xl p-4 relative">
              <button
                onClick={() =>
                  setUiState((prev) => ({
                    ...prev,
                    showPayrollSchedule: false,
                  }))
                }
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-5 w-5" />
              </button>
              <PayrollScheduleTable />
            </div>
          </div>
        )}

        {/* Custom Payroll Modal */}
        <CustomPayrollModal
          clinician={clinician}
          selectedPeriod={initialSelectedPeriod}
          isOpen={isCustomModalOpen}
          onClose={toggleCustomPayrollModal}
          editingPayroll={editingPayroll}
          onUpdateSuccess={fetchData}
        />

        {/* Payroll Slide Over */}
        {isPayrollSlideOver && selectedAppointment && (
          <PayrollSlideOver
            isOpen={isPayrollSlideOver}
            appointment={selectedAppointment}
            onClose={() => {
              setUiState((prev) => ({
                ...prev,
                isPayrollSlideOver: false,
              }));
              fetchData();
            }}
            selectedPeriod={initialSelectedPeriod.payPeriodNumber}
            selectedPeriodDates={initialSelectedPeriod.includeDates}
            isHr={true}
            onUpdateSuccess={fetchData}
          />
        )}
      </Dialog>
    </Transition.Root>
  );
}
